// Copyright (c) Microsoft. All rights reserved.

import { Product } from '../../../embed-script/HostContext';
import { AuthHelper, AuthType } from '../../../libs/auth/AuthHelper';
import { AlertType } from '../../../libs/models/AlertType';
import { AgentOverrides } from '../../../libs/models/AgentConfig';
import { IChatUser } from '../../../libs/models/ChatUser';
import { ContentMetadata } from '../../../libs/models/ContentMetadata';
import { ServiceInfo } from '../../../libs/models/ServiceInfo';
import { TokenUsage } from '../../../libs/models/TokenUsage';
import { AppState as AppStateEnum } from '../../../AppState';
import { AiProviderSelection } from '../../../libs/models/AiProvider';
import { UserPreferences, defaultUserPreferences } from '../../../libs/models/UserPreferences';
import { UserMemory, defaultUserMemory } from '../../../libs/models/UserMemory';

// This is the default user information when authentication is set to 'None'.
// It must match what is defined in PassthroughAuthenticationHandler.cs on the backend.
export const DefaultChatUser: IChatUser = {
    id: 'c05c61eb-65e4-4223-915a-fe72b0c9ece1',
    emailAddress: 'user@contoso.com',
    fullName: 'Default User',
    online: true,
};

export const DefaultActiveUserInfo: ActiveUserInfo = {
    id: DefaultChatUser.id,
    email: DefaultChatUser.emailAddress,
    username: DefaultChatUser.fullName,
    sessionId: null,
};

export interface ActiveUserInfo {
    id: string;
    email: string;
    username: string;
    sessionId: string | null;
}

export interface Alert {
    message: string;
    type: AlertType;
    id?: string;
    onRetry?: () => void;
}

export interface Setting {
    title: string;
    description?: string;
    stackVertically?: boolean;
    learnMoreLink?: string;
}

export interface FeedbackContext {
    chatId?: string;
    messageId?: string;
    contentMetadata?: ContentMetadata[];
    url?: string;
    pageTitle?: string;
    hostId?: string;
    products?: Product[];
}

interface FeedbackForm {
    isOpen: boolean;
    isSending: boolean;
    value: string;
    isPositive?: boolean;
    context: FeedbackContext;
    rating: string | null;
    hideRating: boolean;
}

export interface AppState {
    currentState: AppStateEnum;
    alerts: Alert[];
    activeUserInfo?: ActiveUserInfo;
    tokenUsage: TokenUsage;
    serviceInfo: ServiceInfo;
    embeddedMode: boolean;
    feedbackForm: FeedbackForm;
    isRateLimited: boolean;
    rateLimitResetTime: number | null;
    showCsat: boolean;
    authToken: string | null;
    aiProviderSelection: AiProviderSelection;
    agentOverrides: AgentOverrides;
    userPreferences: UserPreferences;
    userMemory: UserMemory;
}

// Load agent overrides from localStorage for persistence across page refreshes
const loadAgentOverridesFromStorage = (): AgentOverrides => {
    try {
        const stored = localStorage.getItem('agentOverrides');
        return stored ? (JSON.parse(stored) as AgentOverrides) : {};
    } catch {
        return {};
    }
};

// Load AI provider selection from localStorage
const loadAiProviderSelectionFromStorage = (): AiProviderSelection => {
    try {
        const stored = localStorage.getItem('aiProviderSelection');
        return stored ? (JSON.parse(stored) as AiProviderSelection) : {};
    } catch {
        return {};
    }
};

export const initialState: AppState = {
    currentState: AppStateEnum.ProbeForBackend,
    alerts: [],
    activeUserInfo: AuthHelper.getAuthType() === AuthType.None ? DefaultActiveUserInfo : undefined,
    tokenUsage: {},
    serviceInfo: {
        featureFlags: {},
    },
    embeddedMode: false,
    feedbackForm: {
        isOpen: false,
        isSending: false,
        value: '',
        isPositive: undefined,
        context: {},
        rating: null,
        hideRating: false,
    },
    isRateLimited: false,
    rateLimitResetTime: null,
    showCsat: false,
    authToken: null,
    aiProviderSelection: loadAiProviderSelectionFromStorage(),
    agentOverrides: loadAgentOverridesFromStorage(),
    userPreferences: defaultUserPreferences,
    userMemory: defaultUserMemory,
};
